import { FieldExtensionComponentProps } from "@backstage/plugin-scaffolder-react";
import { useEntraOAuthContext } from '../../hooks/useEntraOAuthContext';
import { ErrorPanel } from '@backstage/core-components';
import { z } from 'zod';
import { makeFieldSchema, useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import { EntraOAuthWrapper } from '../EntraOAuthWrapper/EntraOAuthWrapper';
import { substituteScopes, substituteTemplate } from '../helpers';
import { Box, Typography } from '@material-ui/core';


const uiOptionsSchema = z.object({
    title: z.string().describe('The title to display to the user. If not specified, the name of the field is used.').optional(),
    scopes: z.array(z.string()).describe('The scopes to use for the token. Scopes can contain placeholders in the form of {key} referencing a property in the form data.'),
    sessionKey: z.string().describe('The key to use for storing the token in session storage.').default('entraOAuthToken'),
    message: z.string().describe('The message to display to the user').optional(),
});

export const EntraOAuthTokenFieldSchema = makeFieldSchema({
	output: zImpl => zImpl.string(),
  uiOptions: zImpl => zImpl.object({
    title: zImpl.string().describe('The title to display to the user. If not specified, the name of the field is used.').optional(),
    scopes: zImpl.array(zImpl.string()).describe('The scopes to use for the token. Scopes can contain placeholders in the form of {key} referencing a property in the form data.'),
    sessionKey: zImpl.string().describe('The key to use for storing the token in session storage.').default('entraOAuthToken'),
    message: zImpl.string().describe('The message to display to the user').optional(),
  }),
});

const throwError = (message: string) => {
    console.error(message);
    throw new Error(message);
}

export const EntraOAuthTokenField = (props: FieldExtensionComponentProps<string, { scopes: string[], sessionKey: string, message?: string }>) => {
    const { formData } = props.formContext;
    const result = uiOptionsSchema.safeParse(props.uiSchema['ui:options']);

    const scopes = substituteScopes(result.data?.scopes ?? [], formData);
    const sessionKey = substituteTemplate<string>(
        result.data?.sessionKey ?? 'entraOAuthToken',
        formData,
        (value) => String(value)
    );
    if (!scopes.isValid) {
        throwError(`Missing required properties: ${scopes.missingProperties.join(', ')}`);
    }

    if(!sessionKey.isValid){
        throwError(`Invalid sessionKey template: ${sessionKey.missingProperties.join(', ')}`);
    }

    const { setSecrets, secrets } = useTemplateSecrets();

    const onTokenChange = (token: string | undefined) => {

        // Related issue: https://github.com/backstage/backstage/issues/28729
        const timeout = setTimeout(() => {
            const internalFormData = props.formContext?.formData;
            if (!internalFormData) return;

            const current = internalFormData[props.name];
            if (token && (!current || secrets[props.name] !== token)) {
                props.onChange('********');
                setSecrets({ [props.name]: token});
            } else if ((!token) && (current || secrets[props.name] !== '')) {
                props.onChange(undefined);
                setSecrets({ [props.name]: ''});
            }
        }, 0);

        return () => clearTimeout(timeout);
    }

    if (result.success) {
        return (
            <EntraOAuthWrapper {...props} scopes={scopes.scopes} sessionKey={sessionKey.result} message={result.data.message} onTokenChange={onTokenChange}>
                <EntraOAuthTokenFieldComponent {...props} />
            </EntraOAuthWrapper>
        )
    }

    return <ErrorPanel error={result.error} />;
};

const EntraOAuthTokenFieldComponent = (
    props: FieldExtensionComponentProps<string> ,
) => {
    const { name } = props;
    const { token } = useEntraOAuthContext();

    if(!token){
        return <></>;
    }

 	return (
        <Box height="100%" width="100%">
            <Box>
                <Typography variant="body2">
                    <b>{props.uiSchema['ui:options']?.title ?? name }</b>
                </Typography>
            </Box>
            <Box paddingRight={1}>
                <Typography>
                    { 'You are already authenticated.' }
                </Typography>
            </Box>
        </Box>
    )

};
